/**
 * Authentication Service
 *
 * Handles all authentication operations using the backend API.
 * JWT tokens are stored in httpOnly cookies (set by the backend).
 * This service only manages non-secret local data (expiresAt, user, deviceId).
 */

import { apiClient } from './api.service';
import { tokenService } from './token.service';
import { tokenRefreshQueue } from './token-refresh-queue';
import type {
  LoginCredentials,
  LoginResponse,
  AuthUser,
  SendOtpCredentials,
  VerifyOtpCredentials,
  SendOtpResponse,
  VerifyAdminOtpResponse,
} from '@/types';

// ============================================================================
// CONSTANTS
// ============================================================================

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1';
const LAST_SUCCESSFUL_REFRESH_KEY = '121pass_last_successful_refresh';

/**
 * El backend explícitamente rechazó el refresh (401/403) → la sesión
 * fue revocada y debemos desloguear. Único caso que debe forzar logout
 * desde el frontend; cualquier otro fallo (network, 5xx, timeout) se
 * considera un "hiccup" y se reintenta.
 */
export class RefreshAuthError extends Error {
  readonly kind = 'auth' as const;
  constructor(message = 'Session revoked') {
    super(message);
    this.name = 'RefreshAuthError';
  }
}

/**
 * Backend inalcanzable (ERR_CONNECTION_REFUSED, CORS block, timeout) o
 * devolviendo 5xx. No hay evidencia de que la sesión esté inválida —
 * mantenemos al usuario logueado y reintentamos.
 */
export class RefreshNetworkError extends Error {
  readonly kind = 'network' as const;
  constructor(message = 'Backend unreachable') {
    super(message);
    this.name = 'RefreshNetworkError';
  }
}

// ============================================================================
// AUTH SERVICE
// ============================================================================
//
// NOTE: El dedup de refreshes concurrentes ahora vive en
// `tokenRefreshQueue.refresh(...)` con timeout hard. Antes había un
// mutex local (`let refreshPromise`) que podía quedar pinned a una
// promise colgada si el fetch se quedaba sin respuesta — bug causante
// del shimmer eterno al navegar rápido entre pestañas.

export const authService = {
  /**
   * Step 1: Send OTP to staff email
   */
  async sendOtp({ email }: SendOtpCredentials): Promise<SendOtpResponse> {
    const response = await apiClient.post<SendOtpResponse>(
      '/auth/admin/otp/send',
      { email },
      { requiresAuth: false },
    );

    if (!response.success || !response.data) {
      throw new Error(
        response.error?.message || 'No se pudo enviar el código',
      );
    }

    return response.data;
  },

  /**
   * Step 2: Verify OTP code. May return one of three modes (discriminated
   * union):
   *   - `logged_in`            → session ready; client navigates to dashboard
   *   - `totp_required`        → client navigates to /auth/totp-challenge
   *                              with the `challengeToken` in location.state
   *   - `totp_setup_required`  → session ready BUT role mandates TOTP setup;
   *                              client forces navigation to /account/security
   *
   * In the two authenticated modes the backend has already written httpOnly
   * auth cookies; we persist the session metadata locally (expiresAt,
   * csrfToken, user). In `totp_required` NO cookies are set and the caller
   * must present the code through the TOTP challenge endpoints.
   */
  async verifyOtp({
    email,
    code,
    trustDevice = false,
  }: VerifyOtpCredentials & { trustDevice?: boolean }): Promise<VerifyAdminOtpResponse> {
    tokenService.clearTokens();

    // Reutilizamos la identidad del navegador si ya existe. Antes se generaba
    // una nueva en cada login, así que cada ingreso desde el mismo equipo
    // creaba una fila nueva en `sessions` —viva 30 días— y la lista de
    // dispositivos vinculados se llenaba de duplicados. Con `getDeviceId()`
    // el upsert por `(userId, deviceId)` actualiza la sesión existente, que
    // es justo el comportamiento que el modelo de sesiones espera.
    const deviceId = tokenService.getDeviceId();

    const response = await apiClient.post<VerifyAdminOtpResponse>(
      '/auth/admin/otp/verify',
      { email, code, deviceId, trustDevice },
      { requiresAuth: false },
    );

    if (!response.success || !response.data) {
      throw new Error(
        response.error?.message || 'Código incorrecto o expirado',
      );
    }

    const data = response.data;

    if (data.mode !== 'totp_required') {
      // logged_in OR totp_setup_required — both are authenticated.
      tokenService.setTokenExpiry(data.expiresAt);
      if (data.csrfToken) tokenService.setCsrfToken(data.csrfToken);
      this.setUserData(data.user);
      this.setLastSuccessfulRefresh(Date.now());
    }

    return data;
  },

  /**
   * Complete the TOTP challenge (second factor) after verifyOtp returned
   * `totp_required`. On success the backend sets auth cookies and we persist
   * session metadata locally.
   */
  async verifyTotp(input: {
    challengeToken: string;
    code: string;
    trustDevice?: boolean;
  }): Promise<LoginResponse> {
    // Mismo navegador que superó el primer factor: `getDeviceId()` devuelve
    // la identidad ya persistida (y sólo crea una si no la hubiera).
    const deviceId = tokenService.getDeviceId();

    const response = await apiClient.post<LoginResponse>(
      '/auth/admin/totp/verify',
      { ...input, deviceId },
      { requiresAuth: false },
    );
    if (!response.success || !response.data) {
      throw new Error(
        response.error?.message || 'Código TOTP incorrecto',
      );
    }

    const { expiresAt, user, csrfToken } = response.data;
    tokenService.setTokenExpiry(expiresAt);
    if (csrfToken) tokenService.setCsrfToken(csrfToken);
    this.setUserData(user);
    this.setLastSuccessfulRefresh(Date.now());

    return response.data;
  },

  /**
   * Fetch the current authenticated staff profile from /auth/me. Used after
   * enabling/disabling TOTP to refresh `totpEnabled` / `totpEnforced` flags.
   * Returns null if unauthenticated (cookies cleared) instead of throwing —
   * the caller typically uses it as a best-effort refresh.
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const response = await apiClient.get<AuthUser>('/auth/me');
      if (!response.success || !response.data) return null;
      this.setUserData(response.data);
      return response.data;
    } catch {
      return null;
    }
  },

  /**
   * Fallback when the staff lost access to their authenticator. Consumes
   * one of the 10 recovery codes (Argon2-verified server-side, single-use).
   */
  async useRecoveryCode(input: {
    challengeToken: string;
    recoveryCode: string;
    trustDevice?: boolean;
  }): Promise<LoginResponse> {
    // Mismo navegador que superó el primer factor: `getDeviceId()` devuelve
    // la identidad ya persistida (y sólo crea una si no la hubiera).
    const deviceId = tokenService.getDeviceId();

    const response = await apiClient.post<LoginResponse>(
      '/auth/admin/recovery-code',
      { ...input, deviceId },
      { requiresAuth: false },
    );
    if (!response.success || !response.data) {
      throw new Error(
        response.error?.message || 'Código de recuperación inválido',
      );
    }

    const { expiresAt, user, csrfToken } = response.data;
    tokenService.setTokenExpiry(expiresAt);
    if (csrfToken) tokenService.setCsrfToken(csrfToken);
    this.setUserData(user);
    this.setLastSuccessfulRefresh(Date.now());

    return response.data;
  },

  /**
   * @deprecated Use sendOtp + verifyOtp instead
   */
  async signIn({ email, password }: LoginCredentials): Promise<LoginResponse> {
    tokenService.clearTokens();

    // Identidad estable del navegador — ver la nota en `verifyOtp`.
    const deviceId = tokenService.getDeviceId();

    const response = await apiClient.post<LoginResponse>(
      '/auth/login',
      { email, password, deviceId },
      { requiresAuth: false },
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Login failed');
    }

    const { expiresAt, user } = response.data;

    tokenService.setTokenExpiry(expiresAt);
    this.setUserData(user);
    this.setLastSuccessfulRefresh(Date.now());

    return response.data;
  },

  /**
   * Sign out the current user.
   * Backend clears httpOnly cookies; we clear local data.
   */
  async signOut(): Promise<void> {
    try {
      const deviceId = tokenService.getExistingDeviceId();
      if (deviceId) {
        try {
          await apiClient.delete(
            `/auth/sessions/${encodeURIComponent(deviceId)}`,
          );
        } catch {
          // Continue with local cleanup even if backend call fails
        }
      }
    } finally {
      tokenService.clearTokens();
      this.clearUserData();
      this.clearLastSuccessfulRefresh();
    }
  },

  /**
   * Get current user from localStorage.
   */
  getUser(): AuthUser | null {
    try {
      const userData = localStorage.getItem('121pass_user');
      if (!userData) return null;

      const parsed = JSON.parse(userData);
      if (!parsed || typeof parsed !== 'object' || !parsed.email) {
        this.clearUserData();
        return null;
      }

      return parsed as AuthUser;
    } catch {
      this.clearUserData();
      return null;
    }
  },

  setUserData(user: AuthUser): void {
    try {
      localStorage.setItem('121pass_user', JSON.stringify(user));
    } catch {
      // Ignore storage errors
    }
  },

  clearUserData(): void {
    try {
      localStorage.removeItem('121pass_user');
    } catch {
      // Ignore storage errors
    }
  },

  isAuthenticated(): boolean {
    return tokenService.isAuthenticated();
  },

  getSession(): { user: AuthUser | null; isAuthenticated: boolean } {
    const isAuthenticated = this.isAuthenticated();
    const user = isAuthenticated ? this.getUser() : null;
    return { user, isAuthenticated };
  },

  /**
   * Refresh access token. Todos los callers (apiClient en 401,
   * AuthContext proactive) pasan por `tokenRefreshQueue` que dedupea
   * concurrent calls y garantiza timeout hard.
   */
  async refreshAccessToken(): Promise<void> {
    return tokenRefreshQueue.refresh(() => this._doRefresh());
  },

  /**
   * Internal: perform the actual token refresh HTTP call.
   * Uses raw fetch() to avoid circular dependency with apiClient.
   *
   * Incluye AbortController con timeout propio (8s) para no confiar
   * solo en el timeout de la queue — si el browser tiene la conexión
   * colgada, el AbortController libera el fetch y evita memory leak.
   */
  async _doRefresh(): Promise<void> {
    const deviceId = tokenService.getExistingDeviceId();

    if (!deviceId) {
      // No device ID = never logueado o localStorage borrado. Esto sí
      // es auth (no hay forma de recuperar sin login).
      throw new RefreshAuthError('No device ID available');
    }

    const csrfToken = tokenService.getCsrfToken();

    // AbortController con timeout para que fetch nunca cuelgue.
    const ctrl = new AbortController();
    const fetchTimeout = setTimeout(() => ctrl.abort(), 8_000);

    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-platform': 'DASHBOARD_ADMIN',
          'x-device-id': deviceId,
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ deviceId }),
        signal: ctrl.signal,
      });
    } catch {
      throw new RefreshNetworkError();
    } finally {
      clearTimeout(fetchTimeout);
    }

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        // Backend dice explícitamente que la sesión no sirve → logout
        tokenService.clearTokens();
        this.clearUserData();
        throw new RefreshAuthError();
      }
      // 500, 502, 503, 504 → gateway/upstream hiccup, no logout
      throw new RefreshNetworkError(`Server returned ${response.status}`);
    }

    let data: { success: boolean; data?: { expiresAt: number; csrfToken?: string } };
    try {
      data = await response.json();
    } catch {
      // Respuesta no parseable — tratamos como network (lo más benigno)
      throw new RefreshNetworkError('Invalid refresh response body');
    }

    if (!data.success || !data.data?.expiresAt) {
      // Backend respondió 200 pero el payload no cuadra. Extremadamente
      // raro; lo tratamos como auth para forzar reautenticación limpia.
      throw new RefreshAuthError('Invalid refresh response');
    }

    // Backend set new httpOnly cookies in the response.
    tokenService.setTokenExpiry(data.data.expiresAt);
    if (data.data.csrfToken) tokenService.setCsrfToken(data.data.csrfToken);
    this.setLastSuccessfulRefresh(Date.now());
  },

  getLastSuccessfulRefresh(): number | null {
    try {
      const value = localStorage.getItem(LAST_SUCCESSFUL_REFRESH_KEY);
      if (!value) return null;
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? null : parsed;
    } catch {
      return null;
    }
  },

  setLastSuccessfulRefresh(timestamp: number): void {
    try {
      localStorage.setItem(LAST_SUCCESSFUL_REFRESH_KEY, timestamp.toString());
    } catch {
      // Ignore storage errors
    }
  },

  clearLastSuccessfulRefresh(): void {
    try {
      localStorage.removeItem(LAST_SUCCESSFUL_REFRESH_KEY);
    } catch {
      // Ignore storage errors
    }
  },
};

export default authService;
